import { useEffect, useState } from 'react';

import { TextField, InputAdornment, ToggleButtonGroup, ToggleButton } from '@material-ui/core';
import PropTypes from 'prop-types';
import useVrackApi from '../../../hooks/useVrackApi';
import { getKitType } from '../details/loading/LoadModal';

const kitSelectDefaultHelperText = 'Choose either an SRR or Release ID number for your kit';
const invalidKitErrorHelperText = 'Invalid Kit provided';
const invalidKitFormatErrorHelperText = 'Invalid Kit format provided';
const failedToValidateKitErrorHelperText = 'Failed to validate the kit. Please try again later';
const kitAlreadyExistsHelperText = 'This kit is already in the list of kits';

SoftwareKitSelect.propTypes = {
  kitID: PropTypes.string,
  setKitID: PropTypes.func,
  kitIDType: PropTypes.string,
  setKitIDType: PropTypes.func,
  kitSystemType: PropTypes.string,
  setKitSystemType: PropTypes.func,
  setNextButtonDisabled: PropTypes.func,
  setAddKitButtonDisabled: PropTypes.func,
  kitList: PropTypes.object
};

export default function SoftwareKitSelect({
  kitID,
  setKitID,
  kitIDType,
  setKitIDType,
  setNextButtonDisabled,
  setKitSystemType,
  kitSystemType,
  setAddKitButtonDisabled,
  kitList
}) {
  const [isKitValid, setIsKitValid] = useState(true);
  const [kitSelectHelperText, setKitSelectHelperText] = useState(kitSelectDefaultHelperText);
  const idType = kitIDType === 'kit_srr' ? 'srr' : 'release_id';
  const kitType = getKitType(kitList);

  const { fetchData: vrackApiForKitSelect } = useVrackApi(
    'get',
    `/scmdb/release/${kitID}?type=${idType}`
  );

  function isKitAlreadyExist(kitList) {
    const isValid = kitList[kitType].some((kit) => {
      if (String(kitID) === String(kit.kit_release_id)) {
        setIsKitValid(false);
        setKitSelectHelperText(kitAlreadyExistsHelperText);
        return true;
      }
      return false;
    });
    return isValid;
  }

  function setAddKitButtonError(validateKitHelperText) {
    setKitSelectHelperText(validateKitHelperText);
    setAddKitButtonDisabled(true);
  }

  function resetKitAddButtonError() {
    setKitSelectHelperText(kitSelectDefaultHelperText);
    if (setNextButtonDisabled) {
      setNextButtonDisabled(false);
    }
  }

  const validateKit = async () => {
    if (kitID === '') {
      setIsKitValid(true);
      resetKitAddButtonError();
      return;
    }
    await vrackApiForKitSelect('get', `/scmdb/release/${kitID}?type=${idType}`)
      .then((resp) => {
        setIsKitValid(true);
        setKitSystemType(resp.systems);
        resetKitAddButtonError();
      })
      .catch((err) => {
        setIsKitValid(false);
        setAddKitButtonDisabled(true);
        if (err.status === 404) {
          setAddKitButtonError(invalidKitErrorHelperText);
        } else if (err.status === 400) {
          setAddKitButtonError(invalidKitFormatErrorHelperText);
        } else {
          setAddKitButtonError(failedToValidateKitErrorHelperText);
        }
        if (setNextButtonDisabled) {
          setNextButtonDisabled(true);
          setAddKitButtonDisabled(true);
        }
      });
    isKitAlreadyExist(kitList);
  };

  useEffect(() => {
    if (kitSystemType) {
      setAddKitButtonDisabled(isKitAlreadyExist(kitList));
    }
    // eslint-disable-next-line
  }, [kitSystemType, setAddKitButtonDisabled, kitID]);

  useEffect(() => {
    validateKit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kitID, kitIDType, setNextButtonDisabled, setKitSystemType, kitSystemType]);

  return (
    <TextField
      fullWidth
      size="small"
      data-testid="kit-id-input"
      id="kit-id-input"
      label="Kit Identifier"
      value={kitID}
      onChange={(event) => {
        setKitID(event.target.value);
      }}
      error={!isKitValid}
      helperText={kitSelectHelperText}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <ToggleButtonGroup
              size="small"
              color="primary"
              value={kitIDType}
              exclusive
              onChange={(event) => {
                setKitIDType(event.target.value);
              }}
            >
              <ToggleButton value="kit_srr" sx={{ borderRadius: '0px' }}>
                SRR
              </ToggleButton>
              <ToggleButton value="kit_release_id">Release ID</ToggleButton>
            </ToggleButtonGroup>
          </InputAdornment>
        )
      }}
      sx={{ '& .MuiInputBase-root': { paddingRight: 0 } }}
    />
  );
}
